import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Card, Row, Col, Button, Table, Tag, Space, Modal, Form, Input, 
  Select, message, Popconfirm, Tooltip, Progress, Badge,
  Typography, Empty, Input as AntInput, Checkbox, Drawer, List
} from 'antd';
import { 
  PlusOutlined, SyncOutlined, DeleteOutlined, 
  PlayCircleOutlined, StopOutlined, FolderOutlined,
  UserOutlined, TeamOutlined, QuestionCircleOutlined,
  SearchOutlined, ReloadOutlined
} from '@ant-design/icons';
import axios from '../../api';
import './cloud-drive-sync.css';
import theme from '../../theme';
import permissionManager from '../../utils/permissions';

const { Title, Text } = Typography;
const { Option } = Select;

const CloudDriveSync = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const [taskForm] = Form.useForm();
  // 进度详情
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [progressTask, setProgressTask] = useState(null);
  const [taskFiles, setTaskFiles] = useState([]);
  const [taskFilesLoading, setTaskFilesLoading] = useState(false);
  const [taskFilesPagination, setTaskFilesPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  
  // 已扫描文件详情
  const [scanFilesDrawerVisible, setScanFilesDrawerVisible] = useState(false);
  const [scanTask, setScanTask] = useState(null);
  const [scanFiles, setScanFiles] = useState([]);
  const [scanFilesLoading, setScanFilesLoading] = useState(false);
  const [scanFilesPagination, setScanFilesPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  
  // 搜索和分页状态
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [spaceFilter, setSpaceFilter] = useState('all');
  
  // 清理垃圾文件状态
  const [cleaningFiles, setCleaningFiles] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => t('common.paginationInfo', { start: range[0], end: range[1], total }),
    pageSizeOptions: ['10', '20', '50', '100']
  });
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // 轮询定时器引用，避免重复与泄漏
  const pollersRef = React.useRef({});

  // 获取当前用户信息
  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await axios.get('/api/userinfo/');
      setCurrentUser(res.data);
    } catch (err) {
      console.error('获取用户信息失败:', err);
    }
  }, []);

  // 加载权限管理器
  const loadPermissions = useCallback(async () => {
    await permissionManager.loadPermissions();
  }, []);

  // 获取同步任务列表
  const fetchTasks = useCallback(async (page = 1, pageSize = 10, search = '', status = 'all', space = 'all', sortBy = 'created_at', sortOrder = 'desc') => {
    try {
      setLoading(true);
      
      // 构建查询参数
      const params = {
        page,
        page_size: pageSize,
        ordering: sortOrder === 'desc' ? `-${sortBy}` : sortBy
      };
      
      // 添加搜索条件
      if (search.trim()) {
        params.search = search.trim();
      }
      
      // 添加状态过滤
      if (status && status !== 'all') {
        params.status = status;
      }
      
      // 添加空间过滤
      if (space && space !== 'all') {
        params.target_group = space;
      }
      
      const res = await axios.get('/api/sync-tasks/', { params });
      
      setTasks(res.data.results || []);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize,
        total: res.data.count || 0
      }));
    } catch (err) {
      console.error('获取同步任务失败:', err);
      message.error(t('common.failedToGetSyncTasks'));
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取用户列表
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get('/api/users/');
      if (Array.isArray(res.data)) {
        setUsers(res.data);
      } else if (Array.isArray(res.data.results)) {
        setUsers(res.data.results);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('获取用户列表失败:', err);
      if (currentUser) {
        setUsers([currentUser]);
      }
    }
  }, [currentUser]);

  // 获取空间列表
  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get('/api/user-accessible-document-groups/');
      setGroups(res.data.results || res.data || []);
    } catch (err) {
      console.error('获取空间列表失败:', err);
    }
  }, []);

  const fetchFolderTree = useCallback(async (groupId) => {
    if (!groupId) { setFolderTree([]); return; }
    setLoadingFolders(true);
    try {
      const res = await axios.get('/api/folders/tree', { params: { group: groupId } });
      setFolderTree(res.data || []);
    } catch (e) {
      console.error('获取空间文件夹树失败:', e);
      setFolderTree([]);
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    loadPermissions();
  }, [fetchCurrentUser, loadPermissions]);

  // 当用户信息和权限都加载完成后，开始拉取数据
  useEffect(() => {
    if (!dataFetched && currentUser && permissionManager.isLoaded) {
      fetchTasks(1, 10, '', 'all', 'all', 'created_at', 'desc');
      fetchUsers();
      fetchGroups();
      setDataFetched(true);
    }
  }, [currentUser, permissionManager.isLoaded, dataFetched, fetchTasks, fetchUsers, fetchGroups]);

  // 弹框打开时，若未选空间且已有空间列表，自动选择并加载文件夹树；若已选，则加载对应文件夹树
  useEffect(() => {
    if (!taskModalVisible) return;
    const gid = taskForm.getFieldValue('target_group_id');
    if (gid) {
      fetchFolderTree(gid);
    } else if (groups && groups.length > 0) {
      const first = groups[0].id;
      taskForm.setFieldsValue({ target_group_id: first, target_folder_id: null });
      fetchFolderTree(first);
    }
  }, [taskModalVisible, groups, fetchFolderTree, taskForm]);

  // 权限与可选用户
  const canManageUsers = permissionManager.hasPermission('user', 'view') || 
    currentUser?.role === 'admin' || 
    currentUser?.role === 'superadmin';

  const getAvailableUsers = () => (canManageUsers ? users : (currentUser ? [currentUser] : []));

  // 任务操作
  const handleAddTask = () => {
    setSelectedTask(null);
    taskForm.resetFields();
    // 公开文件夹模式（无授权）默认开启
    taskForm.setFieldsValue({
      is_public_folder: true,
    });
    if (currentUser && !canManageUsers) {
      taskForm.setFieldsValue({ target_owner_id: currentUser.id });
    }
    // 若已有空间列表，默认选第一个并加载其文件夹树
    if (groups && groups.length > 0) {
      const gid = groups[0].id;
      taskForm.setFieldsValue({ target_group_id: gid, target_folder_id: null });
      fetchFolderTree(gid);
    }
    setTaskModalVisible(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    let folderUrl = '';
    if (task.is_public_folder && task.folder_id) {
      folderUrl = `https://drive.google.com/drive/folders/${task.folder_id}`;
    }
    taskForm.setFieldsValue({
      ...task,
      // 强制公开文件夹模式
      is_public_folder: true,
      folder_url: folderUrl,
      target_owner_id: task.target_owner?.id,
      target_group_id: task.target_group?.id,
      target_folder_id: task.target_folder?.id || null,
    });
    if (task.target_group?.id) {
      fetchFolderTree(task.target_group.id);
    }
    setTaskModalVisible(true);
  };

  const handleDeleteTask = async (task) => {
    try {
      await axios.delete(`/api/sync-tasks/${task.id}/`);
      message.success(t('common.syncTaskDeleteSuccess'));
      fetchTasks(pagination.current, pagination.pageSize, searchText, statusFilter, spaceFilter, sortField, sortOrder);
    } catch (err) {
      console.error('删除同步任务失败:', err);
              message.error(t('common.syncTaskDeleteFailed'));
    }
  };

  const startStatusPolling = (taskId) => {
    // 若已有该任务的轮询，先清理
    if (pollersRef.current[taskId]) {
      clearInterval(pollersRef.current[taskId]);
      delete pollersRef.current[taskId];
    }
    let lastProgress = null;
    let stagnantCount = 0;
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/sync-tasks/${taskId}/`);
        const taskData = response.data;
        setTasks(prev => prev.map(item => (item.id === taskId ? taskData : item)));
        // 监控进度停滞
        if (typeof taskData.progress === 'number') {
          if (taskData.progress === lastProgress) stagnantCount += 1; else { stagnantCount = 0; lastProgress = taskData.progress; }
        }
        const shouldStop = ["completed", "failed", "cancelled"].includes(taskData.status) || stagnantCount >= 30; // ~60秒无进展
        if (shouldStop) {
          clearInterval(pollInterval);
          delete pollersRef.current[taskId];
          if (taskData.status === 'completed') message.success(t('common.syncTaskCompleted'));
          if (taskData.status === 'failed') message.error(t('common.syncTaskFailed', { error: taskData.error_message || t('common.unknownError') }));
          if (taskData.status === 'cancelled') message.info(t('common.syncTaskCancelled'));
        }
      } catch (err) {
        console.error('轮询任务状态失败:', err);
        clearInterval(pollInterval);
        delete pollersRef.current[taskId];
      }
    }, 2000);
    pollersRef.current[taskId] = pollInterval;
    // 将轮询超时延长到5分钟，避免长任务看起来无进度
    setTimeout(() => { if (pollersRef.current[taskId]) { clearInterval(pollersRef.current[taskId]); delete pollersRef.current[taskId]; } }, 5 * 60 * 1000);
  };

  const handleStartSync = async (task) => {
    try {
      const response = await axios.post(`/api/sync-tasks/${task.id}/start/`);
              message.success(response.data.message || t('common.syncTaskStarted'));
      startStatusPolling(task.id);
      fetchTasks(pagination.current, pagination.pageSize, searchText, statusFilter, spaceFilter, sortField, sortOrder);
    } catch (err) {
      console.error('启动同步失败:', err);
              message.error(err.response?.data?.error || t('common.startSyncFailed'));
    }
  };

  const handleCancelSync = async (task) => {
    try {
      await axios.post(`/api/sync-tasks/${task.id}/cancel/`);
              message.success(t('common.syncTaskCancelled'));
      fetchTasks(pagination.current, pagination.pageSize, searchText, statusFilter, spaceFilter, sortField, sortOrder);
    } catch (err) {
      console.error('取消同步失败:', err);
              message.error(t('common.cancelSyncFailed'));
    }
  };

  // 打开进度文件列表
  const openProgress = async (task, page = 1, pageSize = 10) => {
    setProgressTask(task);
    setProgressModalVisible(true);
    setTaskFilesLoading(true);
    try {
      const res = await axios.get('/api/sync-task-files/', { params: { sync_task: task.id, page, page_size: pageSize } });
      const data = res.data;
      setTaskFiles(data.results || []);
      setTaskFilesPagination({ current: page, pageSize, total: data.count || 0 });
    } catch (e) {
              message.error(t('common.failedToGetSyncFiles'));
      setTaskFiles([]);
      setTaskFilesPagination({ current: 1, pageSize: 10, total: 0 });
    } finally {
      setTaskFilesLoading(false);
    }
  };

  const handleTaskFilesPageChange = (page, pageSize) => {
    if (progressTask) {
      openProgress(progressTask, page, pageSize);
    }
  };

  // 打开已扫描文件列表
  const openScanFiles = async (task, page = 1, pageSize = 20) => {
    setScanTask(task);
    setScanFilesDrawerVisible(true);
    setScanFilesLoading(true);
    try {
      const res = await axios.get('/api/sync-task-files/', { 
        params: { 
          sync_task: task.id, 
          page, 
          page_size: pageSize,
          include_all: true // 包含所有扫描到的文件，不仅仅是已同步的
        } 
      });
      const data = res.data;
      setScanFiles(data.results || []);
      setScanFilesPagination({ current: page, pageSize, total: data.count || 0 });
    } catch (e) {
      message.error(t('common.failedToGetScanFiles'));
      setScanFiles([]);
      setScanFilesPagination({ current: 1, pageSize: 20, total: 0 });
    } finally {
      setScanFilesLoading(false);
    }
  };

  const handleScanFilesPageChange = (page, pageSize) => {
    if (scanTask) {
      openScanFiles(scanTask, page, pageSize);
    }
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchTasks(1, pagination.pageSize, value, statusFilter, spaceFilter, sortField, sortOrder);
  };

  // 处理状态过滤
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchTasks(1, pagination.pageSize, searchText, value, spaceFilter, sortField, sortOrder);
  };

  // 处理空间过滤
  const handleSpaceFilter = (value) => {
    setSpaceFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchTasks(1, pagination.pageSize, searchText, statusFilter, value, sortField, sortOrder);
  };

  // 处理分页变化
  const handlePaginationChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
    fetchTasks(page, pageSize, searchText, statusFilter, spaceFilter, sortField, sortOrder);
  };

  // 处理表格排序
  const handleTableChange = (pagination, filters, sorter) => {
    const { field, order } = sorter;
    if (field && order) {
      setSortField(field);
      setSortOrder(order === 'descend' ? 'desc' : 'asc');
      fetchTasks(pagination.current, pagination.pageSize, searchText, statusFilter, spaceFilter, field, order === 'descend' ? 'desc' : 'asc');
    }
  };

  // 重置搜索和过滤
  const handleReset = () => {
    setSearchText('');
    setStatusFilter('all');
    setSpaceFilter('all');
    setSortField('created_at');
    setSortOrder('desc');
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchTasks(1, pagination.pageSize, '', 'all', 'all', 'created_at', 'desc');
  };

  // 清理垃圾文件
  const handleCleanupTempFiles = async () => {
    Modal.confirm({
      title: '确认清理垃圾文件',
      content: (
        <div>
          <p>此操作将清理以下类型的文件：</p>
          <ul>
            <li>同步过程中产生的临时文件</li>
            <li>超过24小时的临时文件</li>
            <li>同步失败时产生的不完整文件</li>
            <li>空目录</li>
          </ul>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>此操作不可撤销，请确认是否继续？</p>
        </div>
      ),
      okText: '确认清理',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          setCleaningFiles(true);
          const response = await axios.post('/api/sync-tasks/cleanup-temp-files/');
          message.success(response.data.message || '垃圾文件清理完成');
        } catch (err) {
          console.error('清理垃圾文件失败:', err);
          message.error(err.response?.data?.error || '清理垃圾文件失败');
        } finally {
          setCleaningFiles(false);
        }
      }
    });
  };

  const handleTaskSubmit = async (values) => {
    try {
      const taskData = { ...values };
      // 强制公开文件夹模式，无需任何账号授权字段
      taskData.is_public_folder = true;
      delete taskData.google_account_id;

      // 从 URL 提取文件夹 ID
      if (taskData.folder_url) {
        const match = taskData.folder_url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
        if (match) {
          taskData.folder_id = match[1];
          // 没有显式名称则用占位名
          if (!taskData.folder_name) {
            taskData.folder_name = `公开文件夹_${taskData.folder_id.slice(0, 8)}`;
          }
        } else {
          message.error(t('common.cannotExtractFolderId'));
          return;
        }
      } else {
        message.error(t('common.pleaseEnterPublicFolderLink'));
        return;
      }

      // 移除表单辅助字段
      delete taskData.folder_url;

      // 目标父文件夹（可选）：映射为 target_folder_id
      // 已在表单中就是 target_folder_id，无需转换

      if (selectedTask) {
        await axios.put(`/api/sync-tasks/${selectedTask.id}/`, taskData);
        message.success(t('common.syncTaskUpdateSuccess'));
      } else {
        await axios.post('/api/sync-tasks/', taskData);
        message.success(t('common.syncTaskCreateSuccess'));
      }

      setTaskModalVisible(false);
      taskForm.resetFields();
      fetchTasks(pagination.current, pagination.pageSize, searchText, statusFilter, spaceFilter, sortField, sortOrder);
      setSelectedTask(null);
    } catch (err) {
      console.error('操作失败:', err);
              message.error(err.response?.data?.error || t('common.operationFailed'));
    }
  };

  // 任务表格列配置（仅公开文件夹场景）
  const taskColumns = [
    {
      title: t('common.taskName'),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      sorter: true,
      render: (text) => (
        <Space>
          <FolderOutlined style={{ color: '#52c41a' }} />
          <span style={{ fontWeight: 'bold' }}>{text}</span>
        </Space>
      )
    },
    {
      title: t('common.source'),
      key: 'source',
      width: 120,
      render: () => <Tag color="orange">{t('common.publicFolder')}</Tag>
    },
    {
      title: t('common.targetUser'),
      dataIndex: ['target_owner', 'username'],
      key: 'target_owner',
      width: 140,
      render: (text) => (
        <Tag icon={<UserOutlined />} color="blue">{text}</Tag>
      )
    },
    {
      title: t('common.targetSpace'),
      dataIndex: ['target_group', 'name'],
      key: 'target_group',
      width: 160,
      render: (text) => text ? (
        <Tag icon={<TeamOutlined />} color="green">{text}</Tag>
      ) : '-'
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 180,
      sorter: true,
      render: (status, record) => {
        const statusConfig = {
          'pending': { color: 'orange', text: t('common.pending') },
          'running': { color: 'blue', text: t('common.syncing') },
          'completed': { color: 'green', text: t('common.completed') },
          'failed': { color: 'red', text: t('common.failed') },
          'cancelled': { color: 'default', text: t('common.cancelled') }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return (
          <Space>
            <Badge status={config.color} />
            <span>{config.text}</span>
            {status === 'running' && (
              record.total_files > 0
                ? <Progress percent={record.progress} size="small" style={{ width: 60 }} showInfo={false} />
                : <Progress percent={99} status="active" size="small" style={{ width: 60 }} showInfo={false} />
            )}
          </Space>
        );
      }
    },
    {
      title: t('common.progress'),
      key: 'progress',
      width: 100,
      render: (_, record) => (
        record.status === 'running' && record.total_files === 0
          ? <Text type="secondary">{t('common.scanning')}（{t('common.synced')} {record.synced_files} {t('common.files')}）</Text>
          : <Text type="secondary">{record.synced_files}/{record.total_files}</Text>
      )
    },
    {
      title: t('common.createdTime'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      sorter: true,
      defaultSortOrder: 'descend',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && (
            <Button 
              type="link" size="small" icon={<PlayCircleOutlined />} 
              onClick={() => handleStartSync(record)}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              {t('common.start')}
            </Button>
          )}
          {record.status === 'running' && (
            <Button 
              type="link" size="small" icon={<StopOutlined />} 
              onClick={() => handleCancelSync(record)}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              {t('common.cancel')}
            </Button>
          )}
          <Button 
            type="link" size="small" onClick={() => openScanFiles(record)}
            style={{ background: theme.BUTTON_BG, color: '#231a17' }}
          >
            {t('common.scannedFiles')}
          </Button>
          {(record.status === 'completed' || record.status === 'failed' || record.status === 'cancelled') && (
            <Button 
              type="link" size="small" icon={<PlayCircleOutlined />} 
              onClick={() => handleStartSync(record)}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              {t('common.restart')}
            </Button>
          )}
          <Button 
            type="link" size="small" onClick={() => handleEditTask(record)}
            style={{ background: theme.BUTTON_BG, color: '#231a17' }}
          >
            {t('common.edit')}
          </Button>
                      <Popconfirm title={t('common.confirmDeleteSyncTask')} onConfirm={() => handleDeleteTask(record)} okText={t('common.confirm')} cancelText={t('common.cancel')}>
              <Button type="link" danger size="small" style={{ background: theme.BUTTON_BG, color: '#231a17' }}>
                {t('common.delete')}
              </Button>
            </Popconfirm>
        </Space>
      )
    }
  ];

  // 组件卸载时清理所有轮询
  useEffect(() => {
    return () => {
      Object.values(pollersRef.current).forEach((iv) => { try { clearInterval(iv); } catch (_) {} });
      pollersRef.current = {};
    };
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Space>
          <Button 
            type="primary" 
            icon={<QuestionCircleOutlined />}
            onClick={() => {
              Modal.info({
                title: t('common.publicDocumentSyncInstructions'),
                width: 700,
                content: (
                  <div>
                    <p><strong>使用步骤：</strong></p>
                    <ol>
                      <li><strong>创建同步任务</strong>：点击"新建任务"，选择目标用户/空间（可选）</li>
                      <li><strong>输入公开文件夹链接</strong>：确保 Google Drive 文件夹已设置为“任何人都可以查看”</li>
                      <li><strong>启动同步</strong>：在任务列表中点击“启动”按钮执行同步</li>
                      <li><strong>监控进度</strong>：查看任务状态和进度信息</li>
                    </ol>
                    <p><strong>注意事项：</strong></p>
                    <ul>
                      <li>本模块已移除授权流程，仅支持公开文件夹</li>
                      <li>文件夹 ID 会自动从链接中提取（/folders/后面的部分）</li>
                      <li>建议使用专门的同步文件夹，避免同步整个 Drive</li>
                    </ul>
                  </div>
                ),
                okText: '我知道了'
              });
            }}
            style={{ background: theme.BUTTON_BG, color: '#231a17' }}
          >
            查看使用说明
          </Button>
          <Text type="secondary">同步公开的 Google Drive 文档到本地系统（无需授权）</Text>
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        {/* 同步任务管理 */}
        <Col xs={24}>
          <Card
            title={
              <Space>
                <SyncOutlined style={{ color: '#52c41a' }} />
                <span>同步任务管理（公开文件夹）</span>
              </Space>
            }
            extra={
              <Space>
                <Button 
                  type="primary"
                  icon={<DeleteOutlined />}
                  onClick={handleCleanupTempFiles}
                  loading={cleaningFiles}
                  style={{ background: '#ff4d4f', borderColor: '#ff4d4f' }}
                >
                  清理垃圾文件
                </Button>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleAddTask}
                  style={{ background: theme.BUTTON_BG, color: '#231a17' }}
                >
                  新建任务
                </Button>
              </Space>
            }
            size="small"
          >
                        {tasks.length > 0 ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Row gutter={16} align="middle">
                    <Col flex="auto">
                      <Space>
                        <AntInput.Search
                          placeholder={t('common.searchTaskNameOrFolderName')}
                          allowClear
                          style={{ width: 300 }}
                          onSearch={handleSearch}
                          onPressEnter={(e) => handleSearch(e.target.value)}
                        />
                        <Select
                          placeholder={t('common.statusFilter')}
                          style={{ width: 120 }}
                          value={statusFilter}
                          onChange={handleStatusFilter}
                          allowClear
                        >
                          <Option value="all">全部状态</Option>
                          <Option value="pending">等待中</Option>
                          <Option value="running">同步中</Option>
                          <Option value="completed">已完成</Option>
                          <Option value="failed">失败</Option>
                          <Option value="cancelled">已取消</Option>
                        </Select>
                        <Select
                          placeholder={t('common.spaceFilter')}
                          style={{ width: 150 }}
                          value={spaceFilter}
                          onChange={handleSpaceFilter}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                        >
                          <Option value="all">全部空间</Option>
                          {groups.map(group => (
                            <Option key={group.id} value={group.id}>{group.name}</Option>
                          ))}
                        </Select>
                        <Button 
                          icon={<ReloadOutlined />} 
                          onClick={() => fetchTasks(pagination.current, pagination.pageSize, searchText, statusFilter, spaceFilter, sortField, sortOrder)}
                          loading={loading}
                        >
                          刷新
                        </Button>
                        <Button 
                          onClick={handleReset}
                          disabled={!searchText && statusFilter === 'all' && spaceFilter === 'all'}
                        >
                          重置
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </div>
                <Table
                  columns={taskColumns}
                  dataSource={tasks}
                  rowKey="id"
                  pagination={pagination}
                  onChange={handleTableChange}
                  loading={loading}
                  size="small"
                  scroll={{ x: 800, y: 400 }}
                />
              </>
            ) : (
              <Empty description="暂无同步任务" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
      </Row>

      {/* 同步任务模态框（仅公开文件夹） */}
      <Modal
        title={selectedTask ? t('common.editSyncTask') : t('common.createSyncTask')}
        open={taskModalVisible}
        onCancel={() => setTaskModalVisible(false)}
        footer={null}
        destroyOnHidden
        width={600}
      >
        <Form form={taskForm} layout="vertical" onFinish={handleTaskSubmit}>
                    <Form.Item name="name" label={t('common.taskName')} rules={[{ required: true, message: t('common.pleaseEnterTaskName') }]}>
            <Input placeholder={t('common.pleaseEnterTaskName')} />
          </Form.Item>

          <Form.Item name="folder_url" label={t('common.googleDrivePublicFolderLink')} rules={[{ required: true, message: t('common.pleaseEnterPublicFolderLink') }]} 
            extra="只需提供公开文件夹链接，系统会自动提取文件夹ID与名称">
            <Input 
              placeholder="https://drive.google.com/drive/folders/..."
              onChange={(e) => {
                const url = e.target.value;
                const match = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
                if (match) {
                  const folderId = match[1];
                  taskForm.setFieldsValue({ folder_id: folderId });
                }
              }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
                        <Form.Item name="target_group_id" label={t('common.targetSpace')}>
            <Select placeholder={t('common.pleaseSelectTargetSpace')} showSearch optionFilterProp="children"
                  onChange={(gid) => { fetchFolderTree(gid); taskForm.setFieldsValue({ target_folder_id: null }); }}
                >
                  {groups.map(group => (
                    <Option key={group.id} value={group.id}>{group.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
                        <Form.Item name="target_owner_id" label={t('common.targetUser')} hidden={!canManageUsers}>
            <Select placeholder={t('common.pleaseSelectTargetUser')} showSearch optionFilterProp="children">
                  {getAvailableUsers().map(user => (
                    <Option key={user.id} value={user.id}>{user.username}</Option>
                  ))}
                </Select>
              </Form.Item>
              {!canManageUsers && currentUser && (
                <Form.Item label={t('common.targetUser')}>
                  <Text type="secondary">{currentUser.username}</Text>
                </Form.Item>
              )}
            </Col>
          </Row>

          <Form.Item name="target_folder_id" label={t('common.targetFolder')}>
            <Select placeholder={loadingFolders ? t('common.loading') : t('common.selectTargetFolder')} allowClear showSearch optionFilterProp="children" disabled={loadingFolders}>
              {/* 展平树为可选项 */}
              {(() => {
                const opts = [];
                const walk = (nodes, prefix = '') => {
                  (nodes || []).forEach(n => {
                    const label = prefix ? `${prefix}/${n.name}` : n.name;
                    opts.push({ id: n.id, label });
                    walk(n.children || [], label);
                  });
                };
                walk(folderTree);
                return opts.map(o => <Option key={o.id} value={o.id}>{o.label}</Option>);
              })()}
            </Select>
          </Form.Item>

          <Form.Item name="use_oss" label="存储方式" valuePropName="checked" initialValue={true}>
            <Checkbox>
              <Space>
                <span>使用阿里云OSS存储</span>
                <Tooltip title="启用后将文件下载到OSS中，提高访问速度和稳定性">
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" className="cloud-primary-btn" htmlType="submit" style={{ background: theme.BUTTON_BG, color: '#231a17' }}>
                {selectedTask ? '更新' : '创建'}
              </Button>
              <Button type="primary" className="cloud-primary-btn cloud-cancel-btn" onClick={() => setTaskModalVisible(false)} style={{ background: theme.BUTTON_BG, color: '#231a17' }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 已同步文件列表 */}
      <Modal
        title={progressTask ? t('common.syncedFilesForTask', { taskName: progressTask.name }) : t('common.syncedFiles')}
        open={progressModalVisible}
        onCancel={() => setProgressModalVisible(false)}
        footer={null}
        destroyOnHidden
        width={800}
      >
        {progressTask && (
          <div style={{ marginBottom: 12 }}>
            <Space size={16}>
              <span>状态：{progressTask.status}</span>
              <span>进度：{progressTask.synced_files}/{progressTask.total_files}</span>
              <span>百分比：{progressTask.progress}%</span>
            </Space>
          </div>
        )}
        <Table
          columns={[
                  { title: t('common.fileName'), dataIndex: 'file_name', key: 'file_name', width: 320 },
      { title: t('common.fileLink'), dataIndex: 'file_url', key: 'file_url', render: (text) => text ? <a href={text} target="_blank" rel="noreferrer">{t('common.open')}</a> : '-' },
      { title: t('common.addedTime'), dataIndex: 'created_at', key: 'created_at', render: (d) => d ? new Date(d).toLocaleString() : '-' }
          ]}
          dataSource={taskFiles}
          rowKey="id"
          loading={taskFilesLoading}
          pagination={{
            current: taskFilesPagination.current,
            pageSize: taskFilesPagination.pageSize,
            total: taskFilesPagination.total,
            onChange: handleTaskFilesPageChange,
            showSizeChanger: false,
          }}
          size="small"
        />
      </Modal>

      {/* 已扫描文件列表 */}
      <Drawer
        title={scanTask ? `已扫描的文件 - ${scanTask.name}` : '已扫描的文件'}
        placement="right"
        width={600}
        open={scanFilesDrawerVisible}
        onClose={() => setScanFilesDrawerVisible(false)}
        destroyOnClose
      >
        {scanTask && (
          <div style={{ marginBottom: 16 }}>
            <Space size={16} wrap>
              <Tag color="blue">状态：{scanTask.status}</Tag>
              <Tag color="green">已同步：{scanTask.synced_files}</Tag>
              <Tag color="orange">总文件：{scanTask.total_files}</Tag>
              {scanTask.status === 'running' && (
                <Tag color="processing">进度：{scanTask.progress}%</Tag>
              )}
            </Space>
          </div>
        )}
        
        <List
          loading={scanFilesLoading}
          dataSource={scanFiles}
          pagination={{
            current: scanFilesPagination.current,
            pageSize: scanFilesPagination.pageSize,
            total: scanFilesPagination.total,
            onChange: handleScanFilesPageChange,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <Space>
                    <span>{item.file_name}</span>
                    {item.is_synced ? (
                      <Tag color="success" size="small">已同步</Tag>
                    ) : (
                      <Tag color="processing" size="small">扫描中</Tag>
                    )}
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">文件ID：{item.file_id}</Text>
                    </div>
                    {item.file_url && (
                      <div>
                        <a href={item.file_url} target="_blank" rel="noreferrer">
                          <Text type="link">查看原文件</Text>
                        </a>
                      </div>
                    )}
                    {item.synced_at && (
                      <div>
                        <Text type="secondary">同步时间：{new Date(item.synced_at).toLocaleString()}</Text>
                      </div>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>
    </div>
  );
};

export default CloudDriveSync; 